import { getDefaultLocale, swapLocaleInPath } from '@tuic/i18n';
import { stringify } from 'qs';

import { separateQueryStringFromPath } from './request/path';

import type { QueryParams } from './request/path';
import type { Locale } from '@tuic/i18n';

export const HOST_APP_MAIN =
  process.env.NEXT_PUBLIC_HOST_APP_MAIN ?? 'https://www.meinschiff.com';
export const HOST_APP_ACCOUNT =
  process.env.NEXT_PUBLIC_HOST_APP_ACCOUNT ?? 'https://konto.meinschiff.com';
export const HOST_APP_TRAVEL =
  process.env.NEXT_PUBLIC_HOST_APP_TRAVEL ??
  'https://www.meinschiff.com/reiseplanung';
export const HOST_APP_DASHBOARD =
  process.env.NEXT_PUBLIC_HOST_APP_DASHBOARD ??
  'https://meinereisen.meinschiff.com';
export const HOST_APP_MYJOURNEY =
  process.env.NEXT_PUBLIC_HOST_APP_MYJOURNEY ??
  'https://meinereise.meinschiff.com';
export const HOST_APP_NEXTGEN =
  process.env.NEXT_PUBLIC_HOST_APP_NEXTGEN ?? 'https://www.meinschiff.com';
export const HOST_APP_LIGHTHOUSE =
  process.env.NEXT_PUBLIC_HOST_APP_LIGHTHOUSE ??
  'https://lighthouse.tuicruises.com';

export const HOST_IBE =
  process.env.NEXT_PUBLIC_HOST_IBE ?? 'https://buchung.meinschiff.com/';

export const HOST_SHOP =
  process.env.NEXT_PUBLIC_HOST_SHOP ?? 'https://shop.meinschiff.com';

export const ENDPOINT_ACCOUNT_API =
  process.env.ENDPOINT_ACCOUNT_API ?? 'https://api.tuicruises.com';

export const ENDPOINT_CMW =
  process.env.ENDPOINT_CMW ?? 'https://cmware.meinschiff.com';

export const ENDPOINT_SHIPDATA =
  process.env.ENDPOINT_SHIPDATA ?? 'https://shipdata.meinschiff.com';

export type AppId =
  | 'account'
  | 'travel'
  | 'main'
  | 'dashboard'
  | 'nextgen'
  | 'msshop';

const account = {
  /**
   * The set of endpoint URLs.
   */
  endpoints: {
    /**
     * Returns the URL of the endpoint which deletes the currently authenticated user.
     */
    deleteUser: `${HOST_APP_ACCOUNT}/api/delete-user`,
    // TODO: Doc
    profile: (idToken: string) =>
      `${HOST_APP_ACCOUNT}/api/profile?idToken=${encodeURIComponent(idToken)}`,
    setLocale: `${HOST_APP_ACCOUNT}/api/user/set-locale`,
  },

  /**
   * Returns the URL of the home page.
   */
  home: (locale?: Locale) =>
    `${HOST_APP_ACCOUNT}/${locale || getDefaultLocale()}/`,

  /**
   * Returns the URL of the favourites page.
   */
  favourites: (locale?: Locale) =>
    `${HOST_APP_ACCOUNT}/${locale || getDefaultLocale()}/favourites`,
};

const dashboard = {
  trips: (locale?: Locale) => {
    return `${HOST_APP_DASHBOARD}/${locale ?? getDefaultLocale()}`;
  },

  bookingDetails: (params: { locale: string; bookingCode: string }) => {
    return `${HOST_APP_DASHBOARD}/${params.locale}/${encodeURIComponent(
      params.bookingCode,
    )}`;
  },

  v3: {
    bookingDetails: (params: { locale: string; bookingCode: string }) =>
      `${HOST_APP_DASHBOARD}/${params.locale}/${encodeURIComponent(
        params.bookingCode,
      )}/3`,
  },
};

const urls = {
  //TODO: Add docs
  app: (
    app?: AppId | string,
    path: string = '/',
    query: QueryParams = {},
    locale?: Locale,
  ) => {
    const urls = {
      account: HOST_APP_ACCOUNT,
      main: HOST_APP_MAIN,
      travel: HOST_APP_TRAVEL,
      dashboard: HOST_APP_DASHBOARD,
      nextgen: HOST_APP_NEXTGEN,
      msshop: HOST_SHOP,
    };

    let url = urls.account;

    if (typeof app !== 'undefined' && app in urls) {
      url = urls[app as AppId];
    }

    const appsWithLocales: AppId[] = ['account', 'dashboard', 'nextgen'];

    // Only add apps which support locales.
    const shouldAddLocale =
      typeof app !== 'undefined' &&
      appsWithLocales.indexOf(app as AppId) > -1 &&
      !path.startsWith('/api/');

    if (!path) {
      if (shouldAddLocale && locale) {
        return url + '/' + swapLocaleInPath('', locale);
      }

      return url;
    }

    const [initialCleanPath, queryFromPath] = separateQueryStringFromPath(path);

    query = {
      ...queryFromPath,
      ...query,
    };

    let cleanPath = initialCleanPath;

    if (shouldAddLocale && locale) {
      cleanPath = swapLocaleInPath(cleanPath, locale);
    }

    if (cleanPath.substring(0, 1) === '/') {
      cleanPath = cleanPath.substring(1);
    }

    url += '/' + cleanPath;

    if (!Object.keys(query).length) {
      return url;
    }

    return url + '?' + stringify(query);
  },

  /**
   * Returns the URL of the TUI customer support.
   */
  customerSupport: `${HOST_APP_MAIN}/kontakt`,

  /**
   * The set of URLs corresponding to @tuic/account-app.
   */
  account,

  //TODO: Add docs.
  accountServiceApi: {
    bookmark: (tripCode?: string) => {
      const url = ENDPOINT_ACCOUNT_API + '/bookmark';

      if (!tripCode) {
        return url;
      }

      return url + '/' + tripCode;
    },

    myTrips: (bookingCode?: string) => {
      const url = ENDPOINT_ACCOUNT_API + '/profile/my-trips';

      if (!bookingCode) {
        return url;
      }

      return url + '/' + bookingCode;
    },

    profile: ENDPOINT_ACCOUNT_API + '/profile',

    profilePreferences: (regionIdentifier?: string) => {
      const url = `${ENDPOINT_ACCOUNT_API}/profile/preferences`;

      if (!regionIdentifier) {
        return url;
      }

      return url + '/' + regionIdentifier;
    },
  },

  /**
   * The URL of the TUI Cruises endpoint.
   */
  accountApi: `${ENDPOINT_ACCOUNT_API}`,

  /**
   * The set of URLs corresponding to the Content Middle Ware (CMW).
   */
  cmw: {
    /**
     * Returns the URL of the endpoint which returns the app ratings.
     */
    appStoreRatings: `${ENDPOINT_CMW}/ws/export/app-rating`,
  },

  dashboard,

  /**
   * Shared url for trip details
   */
  tripDetails: (params: {
    locale: Locale;
    tripCode: string;
    adults?: number;
    children?: number;
    cabinType?: string;
  }) => {
    const searchParams = new URLSearchParams(
      params as unknown as Record<string, string>,
    );
    searchParams.delete('locale');

    return `${HOST_APP_MAIN}/kreuzfahrt-buchen/reiserouten-details?${searchParams.toString()}`;
  },

  /**
   * The set of URLs corresponding to the main TUIC website.
   */
  main: {
    /**
     * Returns the URL of the contact page.
     */
    contact: `${HOST_APP_MAIN}/kontakt`,

    /**
     * Returns the URL to the page where users can find the routes.
     */
    cruiseFinder: `${HOST_APP_MAIN}/kreuzfahrt-buchen`,

    /**
     * Returns the URL to the page where users can find the destinations.
     */
    destinations: `${HOST_APP_MAIN}/kreuzfahrten`,

    /**
     * Returns the URL of the global home page.
     */
    home: `${HOST_APP_MAIN}/`,

    /**
     * Returns the URL of the newsletter subscription page.
     */
    newsletterSubscribePage: (locale?: Locale) =>
      `${HOST_APP_NEXTGEN}/${locale || getDefaultLocale()}/newsletter/signup`,

    /**
     * Returns the URL of the all-inclusive page.
     */
    inclusive: (locale?: Locale) => {
      if (locale || getDefaultLocale() === 'de') {
        return `${HOST_APP_NEXTGEN}/de/inklusivleistungen `;
      }
      return `${HOST_APP_NEXTGEN}/en/inclusive-benefits`;
    },

    /**
     * Returns the URL of the sale page.
     */
    sale: `${HOST_APP_MAIN}/kreuzfahrten-angebote`,
  },

  contact: (locale: Locale) => {
    if (locale === 'de') {
      return `${HOST_APP_NEXTGEN}/de/kontakt`;
    }
    return `${HOST_APP_NEXTGEN}/en/contact`;
  },

  mainLocalized: {
    home: (locale: Locale) => {
      if (locale === 'de') {
        return `${HOST_APP_MAIN}/de`;
      }
      return `${HOST_APP_NEXTGEN}/en`;
    },
  },

  /**
   * Get the url of the tealium `utag.js` script
   * with profile and environment set based on environment variables.
   *
   * @returns The url of the `utag.js` script
   */
  tealiumScriptUrl: `https://d.meinschiff.com/${
    process.env.NEXT_PUBLIC_TEALIUM_PROFILE ?? 'main'
  }/${process.env.NEXT_PUBLIC_TEALIUM_ENV_FLAG ?? 'prod'}/utag.js`,

  /**
   * The URLs related to the `travel-app`.
   */
  travel: {
    //TODO: Document params
    /**
     * The URL to the booking page.
     */
    ibeBooking: (
      tripCode: string,
      ePackageCode: string,
      config: {
        cabinCode?: string;
        pax: {
          adults?: number;
          children?: number;
        };
        flight?: {
          outbound?: string;
        };
      },
      backLink?: string,
    ) =>
      `${HOST_IBE}?${stringify({
        tripCode,
        ePackageCode,
        cabinType: config.cabinCode,
        adults: config.pax.adults,
        childs: config.pax.children,
        airport: config.flight?.outbound,
        backLink,
      })}`,

    /**
     * Redirects to the booking system applying any bookmark stored for given trip code.
     */
    bookBookmark: (tripCode: string, backlink?: string) =>
      `${HOST_APP_TRAVEL}/api/book?tripCode=${encodeURIComponent(
        tripCode,
      )}&backlink=${encodeURIComponent(backlink ?? '')}`,

    //TODO: Document params
    /**
     * Redirects to the booking system applying given configuration options to given trip code.
     */
    bookPlan: (
      tripCode: string,
      options?: {
        pax?: [number, number];
        adults?: number;
        children?: number;
        inboundAirport?: string;
        outboundAirport?: string;
        cabin?: string;
        cabinType?: string;
        tariff?: string;
        backLink?: string;
      },
    ) =>
      `${HOST_APP_TRAVEL}/api/book-plan?${stringify({
        tripCode,
        adults: options?.adults ?? options?.pax?.[0],
        children: options?.children ?? options?.pax?.[1],
        inboundAirport: options?.inboundAirport,
        outboundAirport: options?.outboundAirport,
        cabin: options?.cabin ?? options?.cabinType,
        tariff: options?.tariff,
        backLink: options?.backLink,
      })}`,

    //TODO: Document params
    /**
     * The URL to the list of bookmarks.
     */
    bookmarks: (query?: { print?: boolean }) =>
      `${HOST_APP_TRAVEL}/merkliste${query ? '?' : ''}${stringify(query)}`,

    //TODO: Document params
    /**
     * The URL of the cruise detail page.
     */
    details: (
      tripCode: string,
      options?: {
        adults?: number;
        children?: number;
        cabinType?: string;
      },
    ) =>
      `${HOST_APP_MAIN}/kreuzfahrt-buchen/reiserouten-details?tripCode=${encodeURIComponent(
        tripCode,
      )}?${stringify({
        adults: options?.adults || 2,
        children: options?.children || 0,
        cabinType: options?.cabinType,
      })}`,

    /**
     * The URL of the `My Journeys` page.
     */
    myJourneys: `${HOST_APP_TRAVEL}/meine-reisen`,

    //TODO: Document params
    /**
     * Link to external `MyJourney` details
     */
    myJourneyDetails: (params: { bookingCode: string }) => {
      return dashboard.bookingDetails({
        locale: 'de',
        bookingCode: params.bookingCode,
      });
    },

    /**
     * The URL to the travel planner based on bookmarks.
     */
    planBookmark: (
      tripCode: string,
      options?: {
        adults?: number;
        children?: number;
      },
    ) =>
      `${HOST_APP_TRAVEL}/merkliste/${encodeURIComponent(tripCode)}?${stringify(
        {
          adults: options?.adults,
          children: options?.children,
        },
      )}`,

    //TODO: Document params
    /**
     * The URL to the travel planner.
     */
    plan: (
      tripCode: string,
      options?: {
        pax?: [number, number];
        adults?: number;
        children?: number;
        inboundAirport?: string;
        outboundAirport?: string;
        cabin?: string;
        cabinType?: string;
        tariff?: string;
        backLink?: string;
        note?: string;
      },
    ) =>
      `${HOST_APP_TRAVEL}/${encodeURIComponent(tripCode)}?${stringify({
        adults: options?.adults ?? options?.pax?.[0],
        children: options?.children ?? options?.pax?.[1],
        inboundAirport: options?.inboundAirport,
        outboundAirport: options?.outboundAirport,
        cabin: options?.cabin ?? options?.cabinType,
        tariff: options?.tariff,
        backLink: options?.backLink,
        note: options?.note,
      })}`,

    //TODO: Document params
    /**
     * The URL to a service which transforms a planning configuration to a bookmark.
     */
    planToBookmark: (
      tripCode: string,
      options?: {
        pax?: [number, number];
        adults?: number;
        children?: number;
        inboundAirport?: string;
        outboundAirport?: string;
        cabin?: string;
        cabinType?: string;
        tariff?: string;
        note?: string;
        preferSignUp?: boolean;
      },
    ) =>
      `${HOST_APP_TRAVEL}/api/plan-to-bookmark?${stringify({
        tripCode,
        adults: options?.adults ?? options?.pax?.[0],
        children: options?.children ?? options?.pax?.[1],
        inboundAirport: options?.inboundAirport,
        outboundAirport: options?.outboundAirport,
        cabin: options?.cabin ?? options?.cabinType,
        tariff: options?.tariff,
        note: options?.note,
        preferSignUp: options?.preferSignUp,
      })}`,

    /**
     * Returns the URL of the pricing models.
     */
    pricingModels: `${HOST_APP_MAIN}/preismodelle`,

    /**
     * The API endpoints.
     */
    endpoints: {
      /**
       * The bookmark proxy.
       */
      bookmark: (tripCode?: string) => {
        const url = `${HOST_APP_TRAVEL}/api/bookmark`;

        if (!tripCode) {
          return url;
        }

        return url + '/' + tripCode;
      },

      //TODO: Document params
      /**
       * The URL of the `My Journeys` API endpoint.
       */
      myJourneys: (idToken: string) =>
        `${HOST_APP_TRAVEL}/api/my-journeys?idToken=${encodeURIComponent(
          idToken,
        )}`,

      /**
       * The cruise list endpoint.
       */
      cruiseList:
        process.env.ENDPOINT_CRUISE_LIST ??
        'https://expertsearch.meinschiff.com/cruisesearch-api/cruiseList',

      //TODO: Document params
      /**
       * All the cabins for given tripCode.
       */
      tripCabins: (tripCode: string) =>
        `${
          process.env.ENDPOINT_CABIN_TYPES_AND_CATEGORIES ??
          'https://cmware.meinschiff.com/ws/ibe-content/cabinTypesAndCategoriesV2'
        }?tripCode=${encodeURIComponent(tripCode)}`,

      //TODO: Document params
      /**
       * The trip info endpoint.
       */
      tripInfo: (tripCode: string) =>
        `${
          process.env.ENDPOINT_TRIP_INFO ??
          'https://cmware.meinschiff.com/ws/ibe-content/tripInfoV2'
        }?tripCode=${encodeURIComponent(tripCode)}`,

      /**
       * Returns the URL of the watchlist endpoint.
       */
      watchlist: (tripCodes: string[]) =>
        `${HOST_APP_MAIN}/form/watchlist.json?ids=${encodeURIComponent(
          tripCodes.join(','),
        )}`,
    },

    /**
     * Returns the URL of the travel app's watchlist endpoint.
     */
    watchlist: `${HOST_APP_TRAVEL}/api/watchlist`,
  },

  shipdata: {
    /**
     * Api endpoint to get ship data like position, speed, course, wind, etc.
     *
     * @param shipId The ship id (as integer)
     */
    shipLog: (shipId: string) =>
      `${ENDPOINT_SHIPDATA}/rest/shipDataService/shipLog/${shipId}`,
  },
};

export default urls;
